import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./reportForm.scss";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import ReportLockContainer from "../../../store/container/ReportLockContainer";
import { ReportLockReducerProps, ReportLockState } from "../../../store/reducers/reportLockReducer";
import {
  clearCaseAssignmentChanged,
  DraftUpdatePayload,
  ReportLockUser,
} from "../../../store/actions/reportLockActions";
import { store } from "../../../store";
import { getHeaders } from "../../../interceptor/axiosInstance";
import { CaseDetail } from "../../../models/CaseDetail/caseDetail.model";
import {
  Button,
  Col,
  Menu,
  Popover,
  Row,
  Modal,
  Input,
  Tooltip,
  Spin,
  Progress, Switch, Tag,
} from "antd";
import AppLoader from "../AppLoader";
import { Report } from "../../../models/Report/report.model";
import { Form, Formik, FormikValues } from "formik";
import Editor from "../Editor";
import CKEditor from "../CKEditor";
import { reportFormValidation } from "./reportFormValidation";
import { ReportTemplate } from "../../../models/ReportTemplate/reportTemplate.model";
import {
  getDropdownOptions,
  IDropdownOptions,
} from "../../utils/dropdownUtils";
import ReportTemplateService from "../../../services/ReportTemplate/reportTemplate.service";
import ReportMacroService from "../../../services/ReportMacro/reportMacro.service";
import ReportMacroForm from "../../../views/ReportMacros/ReportMacroForm";
import { ReportMacro } from "../../../models/ReportMacro/reportMacro.model";
import DropdownField from "../DropdownField";
import ReportService from "../../../services/Report/report.service";
import {
  EyeOutlined,
  DownloadOutlined,
  SoundOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  CheckOutlined,
  RobotFilled,
  CloseSquareFilled,
  FullscreenOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { DownloadFileTypeEnum } from "../../../enums/downloadFileType.enum";
import AdminCaseService from "../../../services/Case/AdminCase/adminCase.service";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import DoctorCaseService from "../../../services/Case/DoctorCase/doctorCase.service";
import { Doctor } from "../../../models/Doctor/doctor.model";
import { Admin } from "../../../models/Admin/admin.model";
import { Case } from "../../../models/Case/case.model";
import { CaseStatusEnum } from "../../../enums/caseStatus.enum";
import Notification from "../Notification";
import { NotificationTypes } from "../../../enums/notificationTypes";

import { setImageWidthFromFigure } from "../../utils/dataFormatConverter";
import ApiRoutes from "../../../routes/routeConstants/apiRoutes";
import UploadDirectReportButton from "../UploadDirectReportButton";
import CaseForm from "../../../views/Cases/ScanCenterCases/CaseForm";
import { DoctorFeatureEnum } from "../../../enums/DoctorFeature.enum";
import { AdminFeatureEnum } from "../../../enums/AdminFeature.enum";
import { AppRoutes } from "../../../routes/routeConstants/appRoutes";
import { generatePath, useLocation } from "react-router-dom";
import { useViewerNotes } from "../../context/ViewerNotesContext";

declare var MediaRecorder: any;
let socket: WebSocket | null = null;
const aiDiagnosisTypes = ["US"]

interface ReportFormProps extends AuthReducerProps, ReportLockReducerProps {
  caseDetailId: number;
  onPublish: () => void;
  user: Doctor | Admin;
  onClose?: () => void;
  fromViewer?: boolean;
  isFullScreen?: boolean;
  content: string;
  values: { content: string };
  setFieldValue: (field: string, value: string) => void;
  errors: { content: string };
}

interface OrganApiResponse {
  data: { id: number; value: string }[];
  message: string;
  status: number;
}

function ReportForm({
  caseDetailId,
  userRole,
  onPublish,
  onClose,
  user,
  values,
  setFieldValue,
  fromViewer = false,
  isFullScreen = false,
  eventId,
  lockEvent,
  lockEventReportId,
  lockEventLockedBy,
  lockEventDraftUpdate,
  assignmentChangedEventId,
  assignmentChangedCaseId,
  assignmentChangedIsRemoved,
}: ReportFormProps) {

  /** Dark UI for chrome (header, buttons, etc.) when report opens beside the DICOM viewer. */
  const viewerReportChrome = !isFullScreen && fromViewer;
  /** CKEditor / TinyMCE always use light theme for readability; chrome can stay dark. */
  const editorViewerDarkMode = false;

  const location = useLocation();
  const viewerNotes = useViewerNotes();
  const appliedPendingNotesAtRef = useRef<number | null>(null);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [caseDetail, setCaseDetail] = useState<CaseDetail>();

  const [formValues, setFormValues] = useState(new Report());

  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);

  const [reportTemplateOptions, setReportTemplateOptions] = useState<
    IDropdownOptions[]
  >([]);

  const [formLoading, setFormLoading] = useState(false);

  const [publishLoading, setPublishLoading] = useState(false);

  const [pdfLoading, setPdfLoading] = useState(false);

  const [wordLoading, setWordLoading] = useState(false);

  const [caseLoading, setCaseLoading] = useState(false);

  const debounceTimerRef: any = useRef(null);

  const formikRef = useRef<any>(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [voiceLoading, setVoiceLoading] = useState(false);

  const [content, setContent] = useState(""); // CKEditor content state
  // const recognitionRef = useRef<SpeechRecognition | null>(null); // Ref to manage SpeechRecognition instance
  const editorInstanceRef = useRef<any>(null);
  const [timer, setTimer] = useState(0); // Timer in seconds
  const [totalTimer, setTotalTimer] = useState(0); // Timer in seconds
  const [sessionTimer, setSessionTimer] = useState(0); // Timer in seconds
  const [isMuted, setIsMuted] = useState(true);
  const [isMicLoading, setIsMicLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [aiReportLoading, setAiReportLoading] = useState(false);
  const [usgReportLoading, setUsgReportLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVoiceToTextEnabled, setVoiceToTextEnabled] = useState(false);
  const [isAiReportingEnabled, setAiReportingEnabled] = useState(false);
  const [aiCreditsUsed, setAiCreditsUsed] = useState(0);
  const [aiTotalCredits, setAiTotalCredits] = useState(0);
  const [isAiReportingAllowed, setAiReportingAllowed] = useState(false);
  const [isVoiceToTextAllowed, setVoiceToTextAllowed] = useState(false);
  const [isDeepgram, setDeepgram] = useState(false);
  const [disableAiReporting, setDisableAiReporting] = useState(false);
  const [disableUsgReporting, setDisableUsgReporting] = useState(false);

  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewExpandedVisible, setPreviewExpandedVisible] = useState(false);
  const previewPdfUrlRef = useRef<string | null>(null);
  const isFullScreenRef = useRef(isFullScreen);
  const isMobileRef = useRef(isMobile);
  isFullScreenRef.current = isFullScreen;
  isMobileRef.current = isMobile;
  const fetchPreviewReportRef = useRef<(reportId: number) => void>(() => {});

  const [liveTranscript, setLiveTranscript] = useState("");
  const [baseValue, setBaseValue] = useState("");
  const sessionTimerRef = useRef(sessionTimer);
  const lastSyncedRef = useRef(0);
  const isSyncingRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isAiDiagnosisEnabled, setAiDiagnosisEnabled] = useState(false);

  const [isTinyEditorEnabled, setTinyEditorEnabled] = useState(false);

  const [textareaValue, setTextareaValue] = useState("");
  const [editorValue, setEditorValue] = useState("");
  const [initialAiDiagnosisValue, setInitialAiDiagnosisValue] = useState("");

  const [template, setTemplate] = useState("");
  const [doctorInput, setDoctorInput] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedOrgan, setSelectedOrgan] = useState(null);
  const [subOptions, setSubOptions] = useState([]);
  const [organOptions, setOrganOptions] = useState<{ value: string; label: string }[]>([]);
  const [subOptionsMap, setSubOptionsMap] = useState<{ [key: string]: { value: string; label: string }[] }>({});

  const [uploadDirectReportModal, setUploadDirectReportModal] = useState(false);

  const [reportMacros, setReportMacros] = useState<ReportMacro[]>([]);
  const [addMacroModalVisible, setAddMacroModalVisible] = useState(false);
  const [activeMacroForAdd, setActiveMacroForAdd] = useState(new ReportMacro());
  const [showEditCaseModal, setShowEditCaseModal] = useState(false);

  // Per-role features: admins use adminFeatures, doctors/typists use doctorFeatures.
  const userFeatures = userRole === UserRoleEnum.ADMIN ? user.adminFeatures ?? [] : user.doctorFeatures ?? [];
  const userFeatureNames = userFeatures.map((feature) => feature.feature);

  // report_lock and auto_save are admin-level features. They gate the feature for every
  // user under that admin — doctors receive their parent admin's adminFeatures in the
  // login response, so checking adminFeatures works for both admins and doctors.
  const adminFeatureNames = (user.adminFeatures ?? []).map((f) => f.feature);

  const isTypistLogin = userFeatureNames.includes(DoctorFeatureEnum.TYPIST_LOGIN);
  const isReportLockEnabled = adminFeatureNames.includes(AdminFeatureEnum.REPORT_LOCK);
  const isAutoSaveEnabled = adminFeatureNames.includes(AdminFeatureEnum.AUTO_SAVE);
  const isDoctorWithApprovalRequired =
    userRole === UserRoleEnum.DOCTOR && (user as Doctor).approvalRequired === true;

  // --- Lock & auto-save state ---
  const [isEditorLocked, setIsEditorLocked] = useState(false);
  const [lockCheckLoading, setLockCheckLoading] = useState(false);
  const [lockedByInfo, setLockedByInfo] = useState<ReportLockUser | null>(null);
  const [lockAvailable, setLockAvailable] = useState(false);
  const [currentUserHasLock, setCurrentUserHasLock] = useState(false);
  const [lockFeatureEnabled, setLockFeatureEnabled] = useState(false);
  const [assignmentChangedWarning, setAssignmentChangedWarning] = useState<"updated" | "removed" | null>(null);

  const warningActionLabel = isFullScreen && assignmentChangedWarning === "removed" ? "Close" : "Refresh";
  const warningActionHandler = isFullScreen && assignmentChangedWarning === "removed" ? () => window.close() : () => window.location.reload();
  const warningMessage =
    assignmentChangedWarning === "removed"
      ? isFullScreen
        ? "⚠️ Assignment removed. Editing disabled — please close this window."
        : "⚠️ Assignment removed. Editing disabled — please refresh."
      : "⚠️ Assignment updated. Please refresh.";
  const [lastAutoSavedAt, setLastAutoSavedAt] = useState<Date | null>(null);
  const [showSavedTick, setShowSavedTick] = useState(false);
  const [lastDraftSavedBy, setLastDraftSavedBy] = useState<{ displayName: string; savedAt: string } | null>(null);

  const isDirtyRef = useRef(false);
  const lastContentChangeAtRef = useRef(0);
  const isAiGeneratingRef = useRef(false);
  const currentUserHasLockRef = useRef(false);
  const isEditorLockedRef = useRef(false);
  const caseDetailRef = useRef<CaseDetail | undefined>(undefined);
  // Suppresses isDirty marking and Formik updates during programmatic content injection
  // (e.g. live draft updates pushed by the lock holder to read-only viewers).
  const isProgrammaticUpdateRef = useRef(false);
  /** Ignore assignment socket events briefly after opening the report (draft creation / status sync often emits CASE_ASSIGNMENT_UPDATED). */
  const assignmentBannerQuietUntilRef = useRef(0);
  const ASSIGNMENT_BANNER_SUPPRESS_MS = 2800;

  useEffect(() => { currentUserHasLockRef.current = currentUserHasLock; }, [currentUserHasLock]);
  useEffect(() => { isEditorLockedRef.current = isEditorLocked || lockCheckLoading; }, [isEditorLocked, lockCheckLoading]);
  useEffect(() => { caseDetailRef.current = caseDetail; }, [caseDetail]);

  // Apply editor read-only state when lock or lock-check-loading changes.
  // Guard both accessors — the editor instance may not be fully initialised yet.
  useEffect(() => {
    const shouldLock = isEditorLocked || lockCheckLoading;
    applyEditorReadOnly(editorInstanceRef.current, shouldLock);
  }, [isEditorLocked, lockCheckLoading, isTinyEditorEnabled]);

  // Acquire lock when report ID becomes available (only if feature is enabled)
  useEffect(() => {
    // Reset any stale assignment warning whenever the report (re-)opens
    setAssignmentChangedWarning(null);

    if (!isReportLockEnabled || !formValues?.id) return;
    const reportId = formValues.id;

    // Disable the editor while the lock check is in flight so the user
    // never sees a false "editable" window before the API responds.
    setLockCheckLoading(true);

    ReportService.acquireReportLock(
      reportId,
      () => {
        setCurrentUserHasLock(true);
        setLockFeatureEnabled(true);
        setIsEditorLocked(false);
        setLockAvailable(false);
        setLockCheckLoading(false);
        setAssignmentChangedWarning(null);
      },
      (error: any) => {
        setLockCheckLoading(false);
        const status = error?.response?.status;
        if (status === 423) {
          setLockFeatureEnabled(true);
          setIsEditorLocked(true);
          setCurrentUserHasLock(false);
          const lb = error?.response?.data?.locked_by;
          if (lb) setLockedByInfo(lb);
        }
        // 404 = feature flag off — fall through silently, keep existing behaviour
      },
      () => {}
    );

    return () => {
      if (currentUserHasLockRef.current) {
        ReportService.releaseReportLock(reportId, () => {}, () => {}, () => {});
        currentUserHasLockRef.current = false;
        setCurrentUserHasLock(false);
      }
    };
  }, [formValues?.id]);

  // Best-effort lock release on tab/window close (only if feature is enabled)
  useEffect(() => {
    if (!isReportLockEnabled) return;
    const handleBeforeUnload = () => {
      if (!currentUserHasLockRef.current || !formValues?.id) return;
      const headers = getHeaders();
      const url =
        (ApiRoutes.BASE_URL ?? "") +
        ApiRoutes.REPORT_LOCK.replace(":reportId", String(formValues.id));
      fetch(url, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [formValues?.id]);

  // React to socket lock events dispatched by AppSidebar (only if feature is enabled)
  useEffect(() => {
    if (!isReportLockEnabled || !lockEvent || !lockEventReportId || lockEventReportId !== formValues?.id) return;

    if (lockEvent === "acquired" && lockEventLockedBy) {
      // If current user acquired the lock, don't show "locked" state or notification
      if (lockEventLockedBy.id === user?.id && lockEventLockedBy.type === userRole) {
        currentUserHasLockRef.current = true;
        setCurrentUserHasLock(true);
        return;
      }
      setIsEditorLocked(true);
      setLockedByInfo(lockEventLockedBy);
      setLockAvailable(false);
      Notification({
        message: `${lockEventLockedBy.display_name} is currently editing this report`,
        type: NotificationTypes.WARNING,
      });
    }

    if (lockEvent === "released") {
      setIsEditorLocked(false);
      setLockedByInfo(null);
      setLockAvailable(true);
      currentUserHasLockRef.current = false;
      setCurrentUserHasLock(false);
      // Re-fetch the latest saved draft so all open viewers see current content
      if (formValues?.id && caseDetailId) {
        const fetchReport =
          caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED
            ? ReportService.showAdminReport
            : ReportService.showReport;
        fetchReport(
          caseDetailId,
          (freshReport: Report) => {
            const freshContent = freshReport.content ?? "";
            // Update Formik state
            setFormValues(freshReport);
            // Push content directly into the mounted editor
            const editorInstance = editorInstanceRef.current;
            if (editorInstance) {
              isProgrammaticUpdateRef.current = true;
              try {
                setEditorContent(editorInstance, freshContent);
              } finally {
                setTimeout(() => { isProgrammaticUpdateRef.current = false; }, 0);
              }
            }
          },
          () => {},
          () => {}
        );
      }
    }

    if (lockEvent === "draft_updated" && lockEventDraftUpdate && !currentUserHasLockRef.current) {
      const { content: draftContent, savedBy, savedAt } = lockEventDraftUpdate;
      const editorInstance = editorInstanceRef.current;
      if (editorInstance) {
        // Suppress onChangeHandler so this programmatic update doesn't mark the
        // viewer's form as dirty or trigger an accidental auto-save.
        isProgrammaticUpdateRef.current = true;
        try {
          setEditorContent(editorInstance, draftContent);
        } finally {
          // Defer clearing so any synchronous change events fired by setContent
          // are still caught by the guard before React processes them.
          setTimeout(() => { isProgrammaticUpdateRef.current = false; }, 0);
        }
      }
      // Also update Formik so the value prop stays in sync (controls the next render)
      formikRef.current?.setFieldValue("content", draftContent);
      setLastDraftSavedBy({ displayName: savedBy?.display_name ?? "", savedAt });
    }
  }, [eventId, lockEvent, lockEventReportId, lockEventLockedBy, lockEventDraftUpdate, formValues?.id, user?.id, userRole]);

  // Stale CASE_ASSIGNMENT_* Redux from the listing + suppress setup noise when the report first loads.
  useLayoutEffect(() => {
    if (!caseDetailId) return;
    store.dispatch(clearCaseAssignmentChanged());
    assignmentBannerQuietUntilRef.current = Date.now() + ASSIGNMENT_BANNER_SUPPRESS_MS;
  }, [caseDetailId]);

  useEffect(() => {
    if (!formValues?.id || !caseDetailId) return;
    assignmentBannerQuietUntilRef.current = Date.now() + ASSIGNMENT_BANNER_SUPPRESS_MS;
  }, [formValues?.id, caseDetailId]);

  // Show a warning banner when the case assignment changes while the editor is open
  useEffect(() => {
    if (!assignmentChangedEventId || !assignmentChangedCaseId) return;
    if (assignmentChangedCaseId !== caseDetailId) return;
    if (Date.now() < assignmentBannerQuietUntilRef.current) return;

    // Do not interrupt the active lock holder with an "Assignment updated.
    // Please refresh." banner. They are the authoritative editor on this
    // report, so another user merely opening / viewing the same case (which
    // can emit CASE_ASSIGNMENT_STATUS_UPDATED, e.g. via draft Report
    // auto-creation on mount) must not force-refresh their editor.
    // Clicking that banner's Refresh would reload the page, fire the
    // beforeunload lock-release, and hand the lock to the next clicker —
    // breaking the exclusivity guarantee the lock is meant to provide.
    // "removed" still needs to surface: the active user has genuinely lost
    // access and cannot keep editing.
    if (!assignmentChangedIsRemoved && currentUserHasLockRef.current) return;

    setAssignmentChangedWarning(assignmentChangedIsRemoved ? "removed" : "updated");
    if (assignmentChangedIsRemoved && isReportLockEnabled) {
      // Lock the editor immediately — user no longer has access and should not
      // be able to save stale data. Also clear the "editor available" banner.
      setIsEditorLocked(true);
      setLockAvailable(false);
      currentUserHasLockRef.current = false;
      setCurrentUserHasLock(false);
    }
  }, [
    assignmentChangedEventId,
    assignmentChangedCaseId,
    caseDetailId,
    assignmentChangedIsRemoved,
    isReportLockEnabled,
  ]);

  // When the viewer iframe captures a canvas screenshot (Ctrl+C), the parent
  // ViewerWrapper dispatches a `viewer-canvas-copy` CustomEvent.  If this
  // ReportForm is mounted (panel is open) we insert the image at the current
  // cursor position so the radiologist gets the image without any extra clicks.
  const isTinyEditorEnabledRef = useRef(isTinyEditorEnabled);
  isTinyEditorEnabledRef.current = isTinyEditorEnabled;

  useEffect(() => {
    const onViewerImageCopy = (e: Event) => {
      const dataUrl = (e as CustomEvent<{ dataUrl: string }>).detail?.dataUrl;
      if (!dataUrl || !editorInstanceRef.current) return;

      if (!isTinyEditorEnabledRef.current) {
        // CKEditor 5 — insert a proper <figure class="image"> wrapper so the
        // image widget renders with the normal toolbar.
        try {
          const editor = editorInstanceRef.current;
          const html = `<figure class="image"><img src="${dataUrl}" style="width:100%;"></figure>`;
          const viewFragment = editor.data.processor.toView(html);
          const modelFragment = editor.data.toModel(viewFragment);
          editor.model.insertContent(modelFragment);
        } catch {}
      } else {
        // TinyMCE — insert with a data-vimg marker so PastePostProcess skips it,
        // then set width directly on the DOM node and re-apply after the 150ms
        // PastePostProcess timeout to guarantee the size sticks.
        try {
          const editor = editorInstanceRef.current;
          const marker = `vimg_${Date.now()}`;
          editor.insertContent(`<p><img data-vimg="${marker}" src="${dataUrl}" /></p>`);

          const applyWidth = () => {
            const body = editor.getBody();
            if (!body) return;
            const imgEl = body.querySelector(`img[data-vimg="${marker}"]`) as HTMLImageElement | null;
            if (imgEl) {
              imgEl.style.width = "100%";
              imgEl.style.height = "auto";
            }
          };

          applyWidth();
          setTimeout(() => {
            applyWidth();
            const body = editor.getBody();
            if (!body) return;
            const imgEl = body.querySelector(`img[data-vimg="${marker}"]`) as HTMLImageElement | null;
            if (imgEl) {
              imgEl.removeAttribute("data-vimg");
              editor.nodeChanged();
            }
          }, 300);
        } catch {}
      }
    };

    window.addEventListener("viewer-canvas-copy", onViewerImageCopy);
    return () => window.removeEventListener("viewer-canvas-copy", onViewerImageCopy);
  }, []);

  // Auto-save: debounced - saves ~10s after user stops typing (triggered by content changes)
  const AUTO_SAVE_DEBOUNCE_MS = 10000;
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleAutoSaveRef = useRef<(() => void) | null>(null);

  const runAutoSave = useCallback(() => {
    if (!isDirtyRef.current) return;
    if (isAiGeneratingRef.current) return;
    if (!currentUserHasLockRef.current) return;
    const values = formikRef.current?.values;
    if (!values) return;

    const report = Object.assign(new Report(), values);
    report.content = removeBgColor(report.content || "");
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    report.caseDetailId = caseDetailRef.current?.id;

    isDirtyRef.current = false;

    ReportService.updateReport(
      report,
      (savedReport: Report) => {
        // Avoid unnecessary Formik reinitialization during autosave. It can clear
        // active TinyMCE selection (e.g. Ctrl+A) while user is still editing.
        const currentValues = formikRef.current?.values;
        const shouldRefreshFormValues =
          !currentValues ||
          currentValues.id !== savedReport.id ||
          currentValues.status !== savedReport.status ||
          currentValues.directReportUpload !== savedReport.directReportUpload;
        if (shouldRefreshFormValues) {
          setFormValues(savedReport);
        }
        const now = new Date();
        setLastAutoSavedAt(now);
        setShowSavedTick(true);
        setTimeout(() => setShowSavedTick(false), 2500);
        if (
          isFullScreenRef.current &&
          !isMobileRef.current &&
          savedReport.id &&
          !savedReport.directReportUpload
        ) {
          fetchPreviewReportRef.current(savedReport.id);
        }
      },
      () => {
        isDirtyRef.current = true;
      },
      () => {},
      true
    );
  }, []);

  useEffect(() => {
    if (!isAutoSaveEnabled || !formValues?.id || formValues?.status) {
      scheduleAutoSaveRef.current = null;
      return;
    }

    scheduleAutoSaveRef.current = () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        runAutoSave();
      }, AUTO_SAVE_DEBOUNCE_MS);
    };

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      scheduleAutoSaveRef.current = null;
    };
  }, [isAutoSaveEnabled, formValues?.id, formValues?.status, runAutoSave]);

  const applyEditorReadOnly = (instance: any, locked: boolean) => {
    if (!instance) return;
    try {
      if (!isTinyEditorEnabled) {
        if ("isReadOnly" in instance) instance.isReadOnly = locked;
      } else {
        if (instance.mode?.set) instance.mode.set(locked ? "readonly" : "design");
      }
    } catch (_) {}
  };

  // Inject content into whichever editor is currently mounted.
  //
  // We MUST duck-type the instance instead of trusting `isTinyEditorEnabled`,
  // because when the user toggles editor type the React flag flips before the
  // new editor's onInit/onReady fires — leaving `editorInstanceRef.current`
  // pointing at the previous (or destroyed) instance. If a socket-driven
  // draft update arrives in that window, calling the wrong API crashes the
  // page (e.g. `o.setContent is not a function`).
  const setEditorContent = (instance: any, content: string) => {
    if (!instance) return;
    try {
      if (typeof instance.setData === "function") {
        // CKEditor: setData() works programmatically even when isReadOnly = true.
        instance.setData(content);
        return;
      }
      if (typeof instance.setContent === "function") {
        // TinyMCE: setContent() is silently ignored in "readonly" mode.
        // Temporarily switch to "design", inject content, then restore readonly.
        const wasReadOnly = instance.mode?.get?.() === "readonly";
        if (wasReadOnly && instance.mode?.set) instance.mode.set("design");
        instance.setContent(content);
        if (wasReadOnly && instance.mode?.set) instance.mode.set("readonly");
      }
    } catch (e) {
      // Stale instance after editor swap or unmount — don't crash the page.
      console.warn("setEditorContent: failed to inject content", e);
    }
  };

  useEffect(() => {
    if (caseDetail) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        handleFetchReportTemplates(searchTerm);
      }, 500);
    }
  }, [searchTerm]);

  const startSpeechRecognition = (
    values: FormikValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    // check if user breaches 5 minute threshold to use voice to text feature
    if (!isVoiceToTextAllowed) {
      Notification({
        message:
          "Voice to text feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return false;
    }
    let silenceTimeout: NodeJS.Timeout | null = null;
    setVoiceLoading(true);
    setIsMicLoading(true);
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      let previousTranscript = "";
      if (!("MediaRecorder" in window)) {
        return alert("Browser not supported");
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      socket = new WebSocket(process.env.REACT_APP_VOICE_BASE_URL! + ApiRoutes.VOICE_TO_TEXT_ENABLE);

      socket.onopen = () => {
        setIsRunning(true);
        setIsMuted(false);
        setIsMicLoading(false);
        Notification({
          message: "Speech detection activated!",
          type: NotificationTypes.SUCCESS,
        });
        mediaRecorder.addEventListener("dataavailable", async (event: any) => {
          if (event.data.size > 0 && socket!.readyState == 1) {
            socket!.send(event.data);
          }
        });
        mediaRecorder.start(1000);
        resetSilenceTimeout(); // Start silence timer
      };

      socket.onmessage = (message) => {
        const transcript = message.data;

        if (transcript) {
          // Append to CKEditor
          // const editorElement = document.querySelector(
          //     ".editor .ck-editor__editable"
          // );
          setTextareaValue((prevValue) => prevValue + " " + transcript);

          // if (document.activeElement === editorElement) {
          //   appendAtCursor(transcript);
          // } else if (document.activeElement === textareaElement) {
          //   // If the textarea is focused, update its value
          //   setTextareaValue((prevValue) => prevValue + transcript);
          // } else {
          //   // Default to CKEditor if nothing else is focused
          //   appendToEnd(transcript);
          // }
        }
        resetSilenceTimeout();
      };

      // Function to append text at the cursor
      const appendAtCursor = (transcript: string) => {
        const editorInstance = editorInstanceRef.current;
        if (editorInstance) {
          editorInstance.model.change((writer: any) => {
            const selection = editorInstance.model.document.selection;
            const insertPosition = selection.focus; // Cursor position
            writer.insertText(transcript + " ", insertPosition);
          });
        }
      };

      // Function to append text at the end of CKEditor
      const appendToEnd = (transcript: string) => {
        const editorInstance = editorInstanceRef.current;
        if (editorInstance) {
          const currentContent = editorInstance.getData(); // Get current content
          let updatedContent = currentContent;

          // Check if the last character is a line break or paragraph tag
          if (
            updatedContent.endsWith("</p>") ||
            updatedContent.endsWith("<br>")
          ) {
            // If it ends with a line break or paragraph, append the transcript within the same block
            updatedContent =
              updatedContent.replace(/<\/p>$/, "") + " " + transcript;
          } else {
            // Otherwise, append the text with a space and keep it within the same paragraph
            updatedContent = updatedContent + " " + transcript;
          }

          // Update the editor content
          editorInstance.setData(updatedContent);
        }
      };

      socket.onclose = () => {
        mediaRecorder.stop();
        Notification({
          message: "Speech detection ended!",
          type: NotificationTypes.SUCCESS,
        });
        // Stop all tracks in the MediaStream to release the microphone
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
        if (silenceTimeout) {
          clearTimeout(silenceTimeout);
          silenceTimeout = null;
        }
        setIsMuted(true);
        setIsRunning(false);
        setSessionTimer(0);
      };

      socket.onerror = (error) => {
        console.log({ event: "onerror", error });
      };
    });

    function resetSilenceTimeout() {
      if (silenceTimeout) clearTimeout(silenceTimeout);
      silenceTimeout = setTimeout(() => {
        // alert(timer)
        // alert(sessionTimer);
        stopSpeechRecognition();
        syncVoiceUsage();
        // setTimer(0);
        setSessionTimer(0);
      }, 30000);
    }
  };

    const startAugnitoSpeechRecognition = (
        values: FormikValues,
        setFieldValue: (field: string, value: any) => void
    ) => {
        if (!isVoiceToTextAllowed) {
            Notification({
                message: "Voice to text feature time limit exceeded. Contact Support Team",
                type: NotificationTypes.ERROR,
            });
            return;
        }

        let silenceTimeout: NodeJS.Timeout | null = null;
        setVoiceLoading(true);
        setIsMicLoading(true);

        let audioContext: AudioContext;
        let processor: ScriptProcessorNode;
        let source: MediaStreamAudioSourceNode;
        let mediaStream: MediaStream;

        socket = new WebSocket(
            process.env.REACT_APP_VOICE_BASE_URL! +
            ApiRoutes.AUGNITO_VOICE_TO_TEXT_ENABLE
        );

        socket.binaryType = "arraybuffer";

        socket.onopen = async () => {
            setIsRunning(true);
            setIsMuted(false);
            setIsMicLoading(false);

            Notification({
                message: "Speech detection activated!",
                type: NotificationTypes.SUCCESS,
            });

            // 🔑 Create AudioContext at 16kHz (MANDATORY)
            audioContext = new AudioContext({ sampleRate: 16000 });

            mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            source = audioContext.createMediaStreamSource(mediaStream);

            processor = audioContext.createScriptProcessor(4096, 1, 1);

            processor.onaudioprocess = (event) => {
                const input = event.inputBuffer.getChannelData(0);

                // Float32 → Int16 PCM
                const pcm16 = new Int16Array(input.length);
                for (let i = 0; i < input.length; i++) {
                    pcm16[i] = Math.max(-1, Math.min(1, input[i])) * 0x7fff;
                }

                // @ts-ignore
                if (socket.readyState === WebSocket.OPEN) {
                    // @ts-ignore
                    socket.send(pcm16.buffer);
                }

                // resetSilenceTimeout();
            };

            source.connect(processor);
            processor.connect(audioContext.destination);
            resetSilenceTimeout();
        };

        socket.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (!data.text) return;

            if (data.type === "partial") {
                setLiveTranscript(data.text);
            }

            if (data.type === "final") {
                // setTextareaValue((prev) => prev + " " + data.text);
                setBaseValue((prev) => (prev + " " + data.text).trim());
                setTextareaValue((prev) =>
                    (prev + " " + data.text).replace(/\s+/g, " ").trim()
                );

                setLiveTranscript("");
            }
            resetSilenceTimeout();
        };


        socket.onclose = () => {
            Notification({
                message: "Speech detection ended!",
                type: NotificationTypes.SUCCESS,
            });

            processor?.disconnect();
            source?.disconnect();
            audioContext?.close();

            if (mediaStream) {
                mediaStream.getTracks().forEach((track) => track.stop());
            }

            if (silenceTimeout) {
                clearTimeout(silenceTimeout);
                silenceTimeout = null;
            }

            setIsMuted(true);
            setIsRunning(false);
            setSessionTimer(0);
        };

        socket.onerror = (error) => {
            console.error("WebSocket error:", error);
        };

        function resetSilenceTimeout() {
            if (silenceTimeout) clearTimeout(silenceTimeout);
            silenceTimeout = setTimeout(() => {
                // alert(timer)
                // alert(sessionTimer);
                stopSpeechRecognition();
                syncVoiceUsage();
                // setTimer(0);
                setSessionTimer(0);
            }, 30000);
        }
    };

    const syncVoiceUsage = (
        latestSeconds?: number
    ) => {
        if (isSyncingRef.current) return; // prevent duplicate

        const current =
            latestSeconds ??
            sessionTimerRef.current;

        const secondsToStore =
            current - lastSyncedRef.current;

        if (secondsToStore < 10) return;

        isSyncingRef.current = true;

        ReportService.storeVoiceToTextSessionData(
            userRole,
            user?.id,
            secondsToStore,
            caseDetail?.id,
            () => {
                lastSyncedRef.current = current;
                isSyncingRef.current = false;
            },
            () => {
                isSyncingRef.current = false;
            },
            () => {
                isSyncingRef.current = false;
            }
        );
    };

    useEffect(() => {
        if (!isRunning) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Prevent duplicate intervals
        if (intervalRef.current) return;

        intervalRef.current = setInterval(() => {
            setTimer((prev) => prev + 1);

            setSessionTimer((prev) => {
                const updated = prev + 1;

                const delta =
                    updated - lastSyncedRef.current;

                if (delta >= 10) {
                    syncVoiceUsage(updated);
                }

                return updated;
            });
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isRunning]);


  function storeVoiceToTextSession() {
    ReportService.storeVoiceToTextSessionData(
      userRole,
      user.id,
      sessionTimer - lastSyncedRef.current,
      caseDetail?.id,
      (response: any) => {
        setVoiceToTextEnabled(true);
        setTimer(response.data.used_credits);
        setTotalTimer(response.data.subscribed_credits);
        setIsMuted(true);
        setIsRunning(false);
        setSessionTimer(0);
        setVoiceToTextAllowed(response.data.allowed);
      },
      () => {},
      () => {}
    );
    setVoiceLoading(false);
  }

  function storeAiReportingSession() {
    ReportService.storeAiReportingSessionData(
      userRole,
      user.id,
      1,
      caseDetail?.id,
      (response: any) => {
        setAiCreditsUsed(response.data.used_credits);
        setAiTotalCredits(response.data.subscribed_credits);
        setAiReportingAllowed(response.data.allowed);
      },
      () => {},
      () => {}
    );
    setVoiceLoading(false);
  }

  const stopSpeechRecognition = () => {
    setIsMicLoading(true);
    if (socket && socket.readyState !== 3) {
      socket.close();
      syncVoiceUsage();
      storeVoiceToTextSession();
      lastSyncedRef.current = 0
    }
      setIsMicLoading(false);
  };

  const removeBgColor = (text: string): string => {
    return text.replace(
      /<span[^>]*style=["'][^"']*background-color:\s*yellow;?[^"']*["'][^>]*>(.*?)<\/span>/gi,
      "$1"
    );
  };

  const styleTableElements = (text: string): string => {
    text= text.replace(
        /<table(?![^>]*style=)/g,
        '<table style="border:1px double #b3b3b3;"'
    );
    text= text.replace(
        /<th\b(?![^>]*style=)/g,
        '<th style="background-color:#fafafa;border:1px double #b3b3b3;"'
    );
    text= text.replace(
        /<td(?![^>]*style=)/g,
        '<td style="border:1px solid transparent;border-color: #d9d9d9"'
    );
    return text;
  };

  const smartReporting = (
    values: FormikValues,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (!isAiReportingAllowed) {
      Notification({
        message:
          "AI reporting feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return false;
    }
    setIsModalOpen(true);
  };

  const generateGeminiReport = async (
    textContent: string,
    values: FormikValues
  ) => {
    try {
      stopSpeechRecognition();
      if (!isAiReportingAllowed) {
        Notification({
          message:
            "Voice to text feature time limit exceeded. Contact Support Team",
          type: NotificationTypes.ERROR,
        });
        return false;
      }
      const report = Object.assign(new Report(), values);
      if (report) {
        isAiGeneratingRef.current = true;
        setAiReportLoading(true);
        setDisableUsgReporting(true);
        const editorInstance = editorInstanceRef.current;
        if (!isTinyEditorEnabled) {
            editorInstance.isReadOnly = true; // Disable editor
            editorInstance.setData(".");
        } else {
            editorInstance.mode.set('readonly');
            editorInstance.setContent(".");
        }
        const editorContent = report.content;
        const shouldAutoSelectTemplate = !report.reportTemplateId;
        ReportService.generateGeminiReport(
          report,
          report.content,
          textContent,
          shouldAutoSelectTemplate ? reportTemplates : [],
          (generatedText: string) => {
            generatedText = generatedText.replace(/```html|```/g, "").trim();
            setEditorContent(editorInstance, generatedText);
            formikRef.current?.setFieldValue("content", generatedText);
            isDirtyRef.current = true;
            lastContentChangeAtRef.current = Date.now();
            isAiGeneratingRef.current = false;
            scheduleAutoSaveRef.current?.();
            applyEditorReadOnly(editorInstance, isEditorLockedRef.current);
            storeAiReportingSession();
            setAiReportLoading(false);
            setDisableUsgReporting(false);
          },
          (errorMessage?: string) => {
            setEditorContent(editorInstance, editorContent ?? "");
            isAiGeneratingRef.current = false;
            applyEditorReadOnly(editorInstance, isEditorLockedRef.current);
            Notification({
              message: errorMessage || "Smart Report generation failed",
              type: NotificationTypes.ERROR,
            });
          },
          () => {
            isAiGeneratingRef.current = false;
            applyEditorReadOnly(editorInstance, isEditorLockedRef.current);
            setAiReportLoading(false);
            setDisableUsgReporting(false);
            // setTextareaValue("");
          }
        );
      }
    } catch (error) {
      isAiGeneratingRef.current = false;
      setAiReportLoading(false);
      setDisableUsgReporting(false);
      console.error("Gemini AI reporting error");
    }
  };

  const handleDownloadReport = (
    status: boolean | undefined,
    fileType: DownloadFileTypeEnum,
    withLetterHead: boolean,
    reportId?: number
  ) => () => {
    if (caseDetail?.id) {
      setPdfLoading(true);
      if (status) {
        ReportService.downloadReport(
          fileType,
          caseDetail,
          withLetterHead,
          reportId ?? 0,
          (reportUrl: string) => {
            window.open(reportUrl, "_blank");
          },
          () => {},
          () => {
            setPdfLoading(false);
            setWordLoading(false);
          }
        );
      } else {
        const apiMethod =
          caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED
            ? ReportService.previewApprovalReport
            : ReportService.previewReport;
        apiMethod(
          caseDetail?.id,
            reportId ?? 0,
          (data: any) => {
            const url = window.URL.createObjectURL(
              new Blob([data], { type: "application/pdf" })
            );
            const pdfWindow = window.open();
            if (pdfWindow) {
              pdfWindow.location.href = url;
              pdfWindow.document.title = "report";
            }
          },
          () => {},
          () => {
            setPdfLoading(false);
            setWordLoading(false);
          }
        );
      }
    }
  };

  const handleViewReport = (
      caseDetail: CaseDetail | undefined,
      reportId?: number | undefined,
  ) => {
    if (caseDetail?.id) {
      const apiMethod = ReportService.previewReport;
      apiMethod(
          caseDetail?.id,
          reportId ?? 0,
          (data: Blob) => {
            const fileURL = window.URL.createObjectURL(
                new Blob([data], { type: "application/pdf" })
            );
            const pdfWindow = window.open(fileURL, "_blank");
            if (pdfWindow) {
              pdfWindow.onload = () => {
                window.URL.revokeObjectURL(fileURL);
              };
            }
          },
          () => {},
          () => {
            setPdfLoading(false);
            setWordLoading(false);
          }
      );
    }
  };

  const handleDownloadDirectReport = (
      caseDetail: CaseDetail | undefined,
      reportId?: number | undefined,
  ) => {
    if (caseDetail?.id) {
      ReportService.downloadDirectReport(
          caseDetail?.id,
          reportId ?? 0,
          (reportUrl: string) => {
            window.open(reportUrl, "_blank");
          },
          () => {},
          () => {
            setPdfLoading(false);
            setWordLoading(false);
          }
      );
    }
  };

  const handlePublishReport = (values: FormikValues) => () => {
    const report = Object.assign(new Report(), values);
    // @ts-ignore
    report.content = removeBgColor(report.content);
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    if (report && report.id) {
      setPublishLoading(true);
      ReportService.publishReport(
        report,
        (report: Report) => {
          setFormValues(report);
          onPublish && onPublish();
        },
        () => {},
        () => {
          setPublishLoading(false);
        }
      );
    }
  };

  const handleApproveAndPublishReport = (values: FormikValues) => () => {
    const report = Object.assign(new Report(), values);
    // @ts-ignore
    report.content = removeBgColor(report.content);
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    if (report && report.id) {
      setPublishLoading(true);
      ReportService.approveAndPublishReport(
        report,
        (report: Report) => {
          setFormValues(report);
          onPublish && onPublish();
        },
        () => {},
        () => {
          setPublishLoading(false);
        }
      );
    }
  };

  const handleSendForApproval = (values: FormikValues) => () => {
    const report = Object.assign(new Report(), values);
    // @ts-ignore
    report.content = removeBgColor(report.content);
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    if (report && report.id) {
      setPublishLoading(true);
      ReportService.sendForApproval(
        report,
        caseDetailId,
        (report: Report) => {
          setFormValues(report);
          onPublish && onPublish();
          onClose && onClose();
        },
        () => {},
        () => {
          setPublishLoading(false);
        }
      );
    }
  };

  const fetchPreviewReport = (reportId: number) => {
    if (!caseDetail?.id) return;
    setPreviewLoading(true);
    if (previewPdfUrlRef.current) {
      window.URL.revokeObjectURL(previewPdfUrlRef.current);
      previewPdfUrlRef.current = null;
      setPreviewPdfUrl(null);
    }
    const apiMethod =
      caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED
        ? ReportService.previewApprovalReport
        : ReportService.previewReport;
    apiMethod(
      caseDetail.id,
      reportId,
      (data: Blob) => {
        const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        previewPdfUrlRef.current = url;
        setPreviewPdfUrl(url);
      },
      () => {},
      () => setPreviewLoading(false)
    );
  };

  fetchPreviewReportRef.current = fetchPreviewReport;

  const handleSubmit = (values: Report) => {
    const report = Object.assign(new Report(), values);
    // @ts-ignore
    report.content = removeBgColor(report.content);
    report.content = setImageWidthFromFigure(report.content);
    report.content = styleTableElements(report.content);
    report.caseDetailId = caseDetail?.id;
    setFormLoading(true);
    if (report.id) {
      ReportService.updateReport(
        report,
        (savedReport: Report) => {
          setFormValues(savedReport);
          isDirtyRef.current = false;
          if (isFullScreen && !isMobile && savedReport.id) fetchPreviewReport(savedReport.id);
        },
        (errorData: any) => {
          if (isReportLockEnabled && errorData?.locked_by) {
            const lb = errorData.locked_by;
            setIsEditorLocked(true);
            setLockedByInfo(lb);
            setCurrentUserHasLock(false);
            Notification({
              message: `Save rejected — ${lb.name} is currently editing this report`,
              type: NotificationTypes.ERROR,
            });
          }
        },
        () => {
          setFormLoading(false);
        }
      );
    } else {
      ReportService.createReport(
        report,
        (createdReport: Report) => {
          setFormValues(createdReport);
          if (isFullScreen && !isMobile && createdReport.id) fetchPreviewReport(createdReport.id);
        },
        () => {},
        () => {
          setFormLoading(false);
        }
      );
    }
  };

  const handleFetchReportTemplates = (searchText: string) => {
    ReportTemplateService.fetchReportTemplates(
      searchText,
      caseDetail?.scanTypeId,
      (reportTemplates: ReportTemplate[]) => {
        setReportTemplates(reportTemplates);
        setReportTemplateOptions(
          getDropdownOptions(reportTemplates, "name", "id")
        );
      },
      () => {},
      () => {}
    );
  };

  const setVoiceAndAiFeature = () => {
    const featuresMap: Record<string, any> = {
      voice_to_text: {
        enabled: false,
        setter: setVoiceToTextEnabled,
        serviceCall: ReportService.getVoiceToTextInfo,
        updateState: (response: any) => {
          setTimer(response.data.used_credits);
          setTotalTimer(response.data.subscribed_credits);
          setSessionTimer(0);
          setVoiceToTextAllowed(response.data.allowed);
        },
      },
      ai_reporting: {
        enabled: false,
        setter: setAiReportingEnabled,
        serviceCall: ReportService.getAiReportingInfo,
        updateState: (response: any) => {
          setAiCreditsUsed(response.data.used_credits);
          setAiTotalCredits(response.data.subscribed_credits);
          setAiReportingAllowed(response.data.allowed);
        },
      },
    };

    const userFeatures =
      userRole === UserRoleEnum.ADMIN
        ? user.adminFeatures
        : user.doctorFeatures;

    let hasAugnito = false;

    userFeatures?.forEach((feature: any) => {
      if (featuresMap[feature.feature]) {
        featuresMap[feature.feature].enabled = true;
      }
      if (feature.feature === "augnito_stt") {
        hasAugnito = true;
      }
      if (feature.feature === "ckeditor") {
        setTinyEditorEnabled(false);
      }
      if(feature.feature === 'tinymce') {
        setTinyEditorEnabled(true);
      }
    });

    setDeepgram(!hasAugnito);
    Object.values(featuresMap).forEach(
      ({ enabled, setter, serviceCall, updateState }) => {
        if (enabled) {
          serviceCall(
            userRole,
            user.id,
            (response: any) => {
              setter(true);
              updateState(response);
            },
            () => {},
            () => {}
          );
        }
      }
    );
  };

  const fetchOrganOptions =  () => {
    try {
      ReportService.getOrganList(
          (response: OrganApiResponse) => {
            const options = response.data.map((item: any) => ({
              value: item.value.replace(/\s+/g, "_").toLowerCase(),
              label: item.value
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c: string) => c.toUpperCase()),
            }));
            setOrganOptions(options);
          },
          (error: any) => {
            console.error("Failed to fetch organ list", error);
          },
          () => {
            console.log("Organ list fetch finished");
          }
      );

    } catch (error) {
      console.error("Failed to fetch organ options:", error);
    }
  };
  
  const fetchOrganPathologyDetails =  () => {
    try {
      ReportService.getOrganPathologyDetails(
          (response: any) => {
            setSubOptionsMap(response.data);
          },
          (error: any) => {
            console.error("Failed to fetch organ list", error);
          },
          () => {
            console.log("Organ list fetch finished");
          }
      );

    } catch (error) {
      console.error("Failed to fetch organ options:", error);
    }
  };

  const handleFetchReportMacros = () => {
    ReportMacroService.fetchReportMacros(
      userRole || UserRoleEnum.DOCTOR,
      (macros: ReportMacro[]) => setReportMacros(macros),
      () => {},
      () => {}
    );
  };

  useEffect(() => {
    if (caseDetail?.id && user && userRole) {
      setVoiceAndAiFeature();
      handleFetchReportTemplates("");
      handleFetchReportMacros();
      if (aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string)){
        fetchOrganOptions();
        fetchOrganPathologyDetails();
      }
      if (caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED) {
        ReportService.showAdminReport(
          caseDetailId,
          (report: Report) => {
            setFormValues(report);
          },
          () => {
          },
          () => {}
        );
      } else {
        ReportService.showReport(
          caseDetailId,
          (report: Report) => {
            setFormValues(report);
          },
          () => {
            const report = new Report();
            report.content = "";
            report.caseDetailId = caseDetail?.id;
            ReportService.createReport(
              report,
              (createdReport: Report) => {
                setFormValues(createdReport);
              },
              () => {},
              () => {},
              true
            );
          },
          () => {}
        );
      }
    }
  }, [caseDetail, user, userRole]);

  // ── Consume pending text pushed from the floating viewer notes panel.
  // Routes to RadAI modal (if AI reporting enabled + allowed) or appends
  // AFTER any existing report content (never replaces).
  useEffect(() => {
    const pending = viewerNotes?.pending;
    if (!pending) return;
    if (!pending.text) {
      viewerNotes?.consume();
      return;
    }
    if (appliedPendingNotesAtRef.current === pending.postedAt) return;
    if (!caseDetail?.id) return;

    const userFeatures =
      userRole === UserRoleEnum.ADMIN
        ? (user as any)?.adminFeatures
        : (user as any)?.doctorFeatures;
    const hasAiFeature = Array.isArray(userFeatures)
      ? userFeatures.some((f: any) => f?.feature === "ai_reporting")
      : false;

    // If user has AI feature, wait for the quota check to resolve before routing.
    if (hasAiFeature && !isAiReportingEnabled) return;

    // For the editor-append path we MUST wait until the report is loaded
    // (showReport / createReport has populated formValues.id) so we don't
    // append to an empty Report() that then gets overwritten when the real
    // content arrives.
    const routeToRadAi =
      hasAiFeature && isAiReportingEnabled && isAiReportingAllowed;
    if (!routeToRadAi && !formValues?.id) return;

    appliedPendingNotesAtRef.current = pending.postedAt;

    if (routeToRadAi) {
      setBaseValue(pending.text);
      setTextareaValue(pending.text);
      setIsModalOpen(true);
    } else {
      const escaped = pending.text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");
      // Pull latest content from formik (reflects the loaded report).
      const prev =
        (formikRef.current?.values?.content as string | undefined) ??
        (formValues?.content as string | undefined) ??
        "";
      const appended = `<p>${escaped}</p>`;
      const next = prev ? `${prev}${appended}` : appended;
      isProgrammaticUpdateRef.current = true;
      formikRef.current?.setFieldValue("content", next);
      setTimeout(() => {
        isProgrammaticUpdateRef.current = false;
      }, 0);
      scheduleAutoSaveRef.current?.();
    }

    viewerNotes?.consume();
  }, [
    viewerNotes,
    caseDetail,
    formValues,
    isAiReportingEnabled,
    isAiReportingAllowed,
    user,
    userRole,
  ]);

  useEffect(() => {
    if (caseDetailId) {
      const showMethod =
        userRole === UserRoleEnum.ADMIN
          ? AdminCaseService.showAdminCase
          : DoctorCaseService.showDoctorCase;
      showMethod(
        caseDetailId,
        (fetchedCaseDetail: CaseDetail) => {
          if (fetchedCaseDetail.cosignEnabled) {
            // Co-sign mode: auto-assign the opening doctor to this case
            const cosignMethod =
              userRole === UserRoleEnum.ADMIN
                ? AdminCaseService.cosignAssign
                : DoctorCaseService.cosignAssign;
            cosignMethod(
              caseDetailId,
              (updatedCaseDetail: CaseDetail) => {
                setCaseDetail(updatedCaseDetail);
              },
              (error: any) => {
                // 423 = report locked by another user — open in read-only mode
                if (error?.response?.status === 423) {
                  setCaseDetail(fetchedCaseDetail);
                  setLockFeatureEnabled(true);
                  setIsEditorLocked(true);
                  setCurrentUserHasLock(false);
                  const lb = error?.response?.data?.locked_by;
                  if (lb) setLockedByInfo(lb);
                } else {
                  // Other failure — still show case in normal mode
                  setCaseDetail(fetchedCaseDetail);
                }
              },
              () => {}
            );
          } else {
            setCaseDetail(fetchedCaseDetail);
          }
        },
        () => {},
        () => {}
      );
    }
  }, [caseDetailId]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "auto"; // ✅ Re-enable scrolling
    } else {
      document.body.style.overflow = ""; // ✅ Reset when modal closes
    }
  }, [isModalOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (formikRef.current && !formValues?.status) {
          formikRef.current.submitForm();
        }
      }
    };
    // Use capture phase so we run before CKEditor/other handlers; also required when form is in a drawer
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [formValues?.status]);

  // Fetch preview once report id is available (report is created when editor opens) - skip on mobile (no left pane)
  useEffect(() => {
    if (isFullScreen && !isMobile && formValues?.id && caseDetail?.id && !formValues?.directReportUpload) {
      fetchPreviewReport(formValues.id);
    }
    return () => {
      if (previewPdfUrlRef.current) {
        window.URL.revokeObjectURL(previewPdfUrlRef.current);
        previewPdfUrlRef.current = null;
      }
    };
  }, [isFullScreen, isMobile, formValues?.id, formValues?.directReportUpload, caseDetail?.id]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const generateUsgReport = (
      values: FormikValues,
      setFieldValue: (field: string, value: any) => void
  ) => {
    if (!isAiDiagnosisEnabled) {
      Notification({
        message:
            "AI reporting feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return false;
    }
    const report = Object.assign(new Report(), values);
    if (report.content === undefined) {
      Notification({
        message: "Please select a report template for generating USG report",
        type: NotificationTypes.ERROR,
      });
      return false;
    }

    isAiGeneratingRef.current = true;
    setUsgReportLoading(true);
    setDisableAiReporting(true);
    const editorInstance = editorInstanceRef.current;
    editorInstance.isReadOnly = true; // Disable editor
    const editorContent = report.content;
    editorInstance.setData(".");

    ReportService.generateUsgReport(
        caseDetail?.id,
        report,
        report.content,
        (generatedText: string) => {
          generatedText = generatedText.replace(/```html|```/g, "").trim();
          editorInstance.setData(generatedText);
          formikRef.current?.setFieldValue("content", generatedText);
          isDirtyRef.current = true;
          lastContentChangeAtRef.current = Date.now();
          isAiGeneratingRef.current = false;
          scheduleAutoSaveRef.current?.();
          applyEditorReadOnly(editorInstance, isEditorLockedRef.current);
          storeAiReportingSession();
          setUsgReportLoading(false);
          setDisableAiReporting(false);
        },
        () => {
          isAiGeneratingRef.current = false;
          applyEditorReadOnly(editorInstance, isEditorLockedRef.current);
          Notification({
            message: "USG Report generation failed",
            type: NotificationTypes.ERROR,
          });
          editorInstance.setData(editorContent);
        },
        () => {
          isAiGeneratingRef.current = false;
          applyEditorReadOnly(editorInstance, isEditorLockedRef.current);
          setUsgReportLoading(false);
          setDisableAiReporting(false);
          setTextareaValue("");
        }
    );

  };

  const handleOrganSelect = (value: any) => {
    setSelectedOrgan(value);

    // populate dependent options
    // @ts-ignore
    setSubOptions(subOptionsMap[value] || []);
    // your logic here
  };

  const handlePathologyDetail = (value: any) => {
    if(value !== undefined) {
      const editorInstance = editorInstanceRef.current;
      if (editorInstance) {
        editorInstance.model.change((writer: any) => {
          const selection = editorInstance.model.document.selection;
          const insertPosition = selection.focus; // Cursor position
          writer.insertText(value + " ", insertPosition);
        });
      }
    }

  }

  // ---- Helpers ----
  const handleMicClick = (
      values: FormikValues,
      setFieldValue: (field: string, value: any) => void
  ) => {
      if (voiceLoading) {
          stopSpeechRecognition();
          return;
      }

      if (isDeepgram) {
          startSpeechRecognition(values, setFieldValue);
      } else {
          startAugnitoSpeechRecognition(values, setFieldValue);
      }

      smartReporting(values, setFieldValue);
  };

  // ---- Tooltip Content ----
  const voiceTooltip = (
      <div>
          <p>
              <strong>How to use Voice-to-Text?</strong>
          </p>
          <ul style={{ paddingLeft: 16 }}>
              <li>Use a microphone for better accuracy</li>
              <li>Speak clearly and pause between words</li>
              <li>
                  Say <code>"comma, colon, semicolon"</code> → <code>, : ;</code>
              </li>
              <li>
                  Say <code>"full stop"</code> → <code>.</code>
              </li>
              <li>
                  Say <code>"next line"</code> → <code>new line</code>
              </li>
          </ul>
      </div>
  );

  return (
    <div
      className={`report-form ${isFullScreen ? "report-form--fullscreen" : ""} ${isFullScreen && isMobile ? "report-form--fullscreen-mobile" : ""} ${!isFullScreen && fromViewer ? "report-form--viewer-drawer" : ""}`}
    >
      {caseLoading ? (
        <AppLoader loading={caseLoading} />
      ) : isFullScreen && !isMobile ? (
        // Fullscreen desktop: left pane (patient, template, buttons) | right pane (editor)
        !formValues?.status ? (
          <Formik
            innerRef={formikRef}
            initialValues={formValues}
            onSubmit={handleSubmit}
            enableReinitialize
            validationSchema={reportFormValidation}
          >
            {({ values, errors, isValid, setFieldValue }) => (
              <Form style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0, height: "100%" }}>
                <div className="report-form__left-pane">
                  {/* Patient Info - name on line 1, age/gender/scan/edit on line 2 */}
                  <div className="left-pane-section">
                    <div className="report-form__patient-block">
                      <strong className="report-form__patient-name">{caseDetail?.patientName}</strong>
                      <div className="report-form__patient-meta">
                        <span style={{ color: "#595959" }}>{caseDetail?.age} / {caseDetail?.gender}</span>
                        <span className="report-form__patient-scan-badge">{caseDetail?.scanTypeName}</span>
                        {caseDetail?.id &&
                          !formValues?.status &&
                          caseDetail?.status !== CaseStatusEnum.REPORTED &&
                          (userRole === UserRoleEnum.ADMIN ||
                            userRole === UserRoleEnum.SCAN_CENTER ||
                            (userRole === UserRoleEnum.DOCTOR &&
                              userFeatureNames.includes(DoctorFeatureEnum.TYPIST_LOGIN))) && (
                          <Tooltip title="Edit Case">
                            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setShowEditCaseModal(true)} style={{ padding: "0 4px" }} />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="left-pane-divider" />

                  {/* Co-sign: Drafted by contributors */}
                  {caseDetail?.cosignEnabled && caseDetail?.cosignContributors && caseDetail.cosignContributors.length > 0 && (
                    <>
                      <div className="left-pane-section">
                        <span className="left-pane-label">Drafted by</span>
                        <div style={{ fontSize: 13, color: "#595959" }}>
                          {caseDetail.cosignContributors.map((c) => c.name).join(", ")}
                        </div>
                      </div>
                      <div className="left-pane-divider" />
                    </>
                  )}

                  {/* Report Actions - Upload & Preview on same line, smaller */}
                  <div className="left-pane-section">
                    <span className="left-pane-label">Report Actions</span>
                    <div className="report-form__actions-row">
                      {!formValues?.status && !isTypistLogin && !isDoctorWithApprovalRequired && (
                        <div style={isEditorLocked || lockCheckLoading || assignmentChangedWarning ? { pointerEvents: "none", opacity: 0.6 } : undefined}>
                          <UploadDirectReportButton
                            reportId={formValues?.id}
                            caseDetail={caseDetail}
                            onSuccess={(report: Report) => {
                              setFormValues(report);
                              onPublish?.();
                            }}
                          />
                        </div>
                      )}
                      {formValues?.id ? (
                        formValues?.status ? (
                          <Popover
                            trigger="click"
                            content={
                              <Menu mode="vertical">
                                {formValues?.directReportUpload ? (
                                  <Menu.Item key="view" onClick={() => handleDownloadDirectReport(caseDetail, formValues?.id)}>Download Uploaded Report</Menu.Item>
                                ) : (
                                  <>
                                    <Menu.Item key="view" onClick={() => handleViewReport(caseDetail, formValues?.id)}>View Report</Menu.Item>
                                    <Menu.Item key="pdf-lh" onClick={handleDownloadReport(formValues?.status, DownloadFileTypeEnum.PDF, true, formValues?.id)}>Download with Letterhead</Menu.Item>
                                    <Menu.Item key="pdf" onClick={handleDownloadReport(formValues?.status, DownloadFileTypeEnum.PDF, false, formValues?.id)}>Download without Letterhead</Menu.Item>
                                    <Menu.Item key="word" onClick={handleDownloadReport(formValues?.status, DownloadFileTypeEnum.WORD, false, formValues?.id)}>Download as Word</Menu.Item>
                                  </>
                                )}
                              </Menu>
                            }
                          >
                            <Button type="primary" loading={pdfLoading} icon={<DownloadOutlined />}>Download</Button>
                          </Popover>
                        ) : (
                          <Tooltip title="Preview PDF">
                            <Button icon={<EyeOutlined />} loading={pdfLoading} disabled={!!assignmentChangedWarning} onClick={handleDownloadReport(formValues?.status, DownloadFileTypeEnum.PDF, false, formValues?.id)}>Preview</Button>
                          </Tooltip>
                        )
                      ) : null}
                    </div>
                  </div>

                  <div className="left-pane-divider" />

                  {/* Report Template */}
                  <div className="left-pane-section">
                    <span className="left-pane-label">Report Template</span>
                    <DropdownField
                      allowClear
                      placeHolder="Select Template"
                      showSearch
                      name="reportTemplateId"
                      disabled={isEditorLocked || lockCheckLoading}
                      onSearch={(v) => setSearchTerm(v)}
                      onClear={() => setSearchTerm("")}
                      onChange={(value) => {
                        setFieldValue("reportTemplateId", value);
                        const template = reportTemplates.find((t) => t.id === value);
                        if (template) setFieldValue("content", values.content ? values?.content + "<br/>" + template.content : template.content);
                      }}
                      options={reportTemplateOptions}
                    />
                  </div>

                  {/* Use New Editor */}
                  <div className="left-pane-section">
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Switch checked={isTinyEditorEnabled} onChange={(c) => setTinyEditorEnabled(c)} style={{ marginRight: 8 }} disabled={aiReportLoading || disableUsgReporting || isEditorLocked || lockCheckLoading} />
                      <span>Use New Editor <Tag color="green" style={{ padding: "0 4px", fontSize: 10 }}>New</Tag></span>
                    </div>
                  </div>

                  {/* RadAI Assist */}
                  {isAiReportingEnabled && (
                    <>
                      <div className="left-pane-divider" />
                      <div className="left-pane-section">
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span className="left-pane-label">RadAI Assist</span>
                          <span style={{ fontSize: 12, color: "#8c8c8c" }}>{aiCreditsUsed} / {aiTotalCredits} credits</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Button
                            block
                            loading={aiReportLoading}
                            disabled={disableAiReporting || isEditorLocked || lockCheckLoading}
                            onClick={() => { if (!isModalOpen) smartReporting(values, setFieldValue); else { setIsModalOpen(false); setIsMicLoading(false); stopSpeechRecognition(); } }}
                          >
                            {!isModalOpen ? "Try RadAI Assist" : "Close RadAI Assist"}
                          </Button>
                          <Tooltip title={<div><p><strong>How to use RadAI Assist?</strong></p><ul style={{ paddingLeft: 16 }}><li>Click <em>Try RadAI Assist</em></li><li>Enter findings (you can dictate template name as well)</li><li>Click <em>Generate Smart Report</em></li></ul></div>} placement="right">
                            <QuestionCircleOutlined style={{ fontSize: 16, cursor: "pointer", color: "#1747E9", flexShrink: 0 }} />
                          </Tooltip>
                        </div>
                        {isModalOpen && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            <Input.TextArea rows={5} placeholder="Enter your key findings or observations" value={isDeepgram ? textareaValue : baseValue + (liveTranscript || "")} style={{ color: !isDeepgram && liveTranscript ? "#999" : "inherit" }} onChange={(e) => { if (isDeepgram) setTextareaValue(e.target.value); else { setBaseValue(e.target.value); setTextareaValue(e.target.value); } }} />
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              {isVoiceToTextEnabled && (
                                <>
                                  <Button type="primary" loading={isMicLoading} onClick={() => handleMicClick(values, setFieldValue)}>{isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}</Button>
                                  <Tooltip title={voiceTooltip} placement="right">
                                    <QuestionCircleOutlined style={{ fontSize: 16, color: "#1747E9", cursor: "pointer" }} />
                                  </Tooltip>
                                  <span style={{ fontSize: 12, color: "#595959" }}>{formatTime(timer)} / {formatTime(totalTimer)}</span>
                                </>
                              )}
                            </div>
                            <Button type="primary" block loading={aiReportLoading} onClick={() => { generateGeminiReport(textareaValue, values); setIsModalOpen(false); }}>Generate Smart Report</Button>
                          </div>
                        )}
                        {(aiReportLoading || usgReportLoading) && (
                          <div style={{ textAlign: "center", padding: "8px 0" }}>
                            <Spin tip="Preparing your report..." />
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* USG Diagnosis */}
                  {isAiDiagnosisEnabled && aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string) && (
                    <>
                      <div className="left-pane-divider" />
                      <div className="left-pane-section">
                        <span className="left-pane-label">USG Diagnosis</span>
                        <DropdownField allowClear placeHolder="Select Organ" showSearch name="organId" onChange={(v) => { setFieldValue("organId", v); handleOrganSelect(v); }} options={organOptions} />
                        <DropdownField allowClear placeHolder="Select Pathology Detail" showSearch name="detailId" onChange={(v) => { setFieldValue("detailId", v); handlePathologyDetail(v); }} options={subOptions} disabled={!selectedOrgan} />
                        <Button type="primary" block loading={usgReportLoading} disabled={disableUsgReporting || isEditorLocked || lockCheckLoading} onClick={() => generateUsgReport(values, setFieldValue)}>Generate USG Report</Button>
                      </div>
                    </>
                  )}

                  {/* Save & Publish */}
                  <div className="left-pane-divider" />
                  <div className="left-pane-section">
                    <span className="left-pane-label">Save &amp; Publish</span>
                    <div className="report-form__submit-wrapper">
                      {lastAutoSavedAt && showSavedTick && (
                        <div className="report-form__autosave-badge report-form__autosave-badge--visible">
                          <span className="report-form__autosave-tick">
                            <svg viewBox="0 0 20 20" fill="none">
                              <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" className="report-form__autosave-circle" />
                              <polyline points="5.5,10.5 8.5,13.5 14.5,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="report-form__autosave-check" />
                            </svg>
                          </span>
                          <span>Auto saved</span>
                        </div>
                      )}
                      {lastAutoSavedAt && !showSavedTick && (
                        <span className="report-form__autosave-static">
                          Last saved at {lastAutoSavedAt.toLocaleTimeString()}
                        </span>
                      )}
                      {!values.status && (
                        <Button type="primary" htmlType="submit" loading={formLoading} disabled={!isValid || formLoading || isEditorLocked || lockCheckLoading} icon={<SaveOutlined />}>Save Draft</Button>
                      )}
                      {values?.id && !values.status && (user?.approvalRequired && (caseDetail?.approvalRequiredForCase ?? true) ? (
                        <Button type="primary" loading={publishLoading} disabled={publishLoading || isEditorLocked || lockCheckLoading} onClick={handleSendForApproval(values)}>Send for Approval</Button>
                      ) : caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED && userRole === UserRoleEnum.ADMIN ? (
                        <Button type="primary" loading={publishLoading} disabled={publishLoading || isEditorLocked || lockCheckLoading} onClick={handleApproveAndPublishReport(values)}>Approve and Publish Report</Button>
                      ) : !isTypistLogin ? (
                        <Button type="primary" loading={publishLoading} disabled={publishLoading || isEditorLocked || lockCheckLoading} icon={<CheckOutlined />} onClick={handlePublishReport(values)}>Save & Publish</Button>
                      ) : null)}
                    </div>
                  </div>

                  {/* Report Preview - updates on Save Draft, fills remaining space */}
                  <div className="left-pane-divider" />
                  <div className="left-pane-section left-pane-section--preview">
                    <span className="left-pane-label">Report Preview</span>
                    <div className="report-form__preview-thumbnail">
                      {formValues?.directReportUpload ? (
                        <div className="report-form__preview-placeholder">
                          Uploaded report
                        </div>
                      ) : !formValues?.id ? (
                        <div className="report-form__preview-placeholder">
                          <Spin size="small" tip="Loading report..." />
                        </div>
                      ) : previewLoading ? (
                        <div className="report-form__preview-placeholder">
                          <Spin size="small" tip="Loading preview..." />
                        </div>
                      ) : previewPdfUrl ? (
                        <div className="report-form__preview-pdf">
                          <iframe src={`${previewPdfUrl}#toolbar=0`} title="Report preview" />
                          <Tooltip title="Expand to fullscreen">
                            <Button
                              type="primary"
                              size="small"
                              icon={<FullscreenOutlined />}
                              className="report-form__preview-expand-btn"
                              disabled={!!assignmentChangedWarning}
                              onClick={() => setPreviewExpandedVisible(true)}
                            />
                          </Tooltip>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="report-form__editor-area">
                  {assignmentChangedWarning && (
                    <div style={{ background: "#fff1f0", border: "1px solid #ffa39e", borderRadius: 4, padding: "8px 14px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ color: "#a8071a" }}>{warningMessage}</span>
                      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                        <Button size="small" type="primary" danger onClick={warningActionHandler}>{warningActionLabel}</Button>
                        {assignmentChangedWarning === "updated" && <Button size="small" onClick={() => setAssignmentChangedWarning(null)}>Dismiss</Button>}
                      </div>
                    </div>
                  )}
                  {lockCheckLoading && (
                    <div style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 6 }}>
                      <Spin size="small" style={{ marginRight: 6 }} /> Checking editor availability…
                    </div>
                  )}
                  {isEditorLocked && !assignmentChangedWarning && (
                    <div style={{ background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 4, padding: "8px 14px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ color: "#ad4e00" }}>🔒 {lockedByInfo?.display_name ?? "Someone"} is currently editing this report</span>
                    </div>
                  )}
                  {lockAvailable && !isEditorLocked && (
                    <div style={{ background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4, padding: "6px 14px", marginBottom: 8, color: "#389e0d", fontSize: 13 }}>
                      Editor is now available — click anywhere in the editor to start editing
                    </div>
                  )}
                  {lastDraftSavedBy && (
                    <div style={{ color: "#8c8c8c", fontSize: 12, marginBottom: 4 }}>
                      Last saved by {lastDraftSavedBy.displayName} at {new Date(lastDraftSavedBy.savedAt).toLocaleTimeString()}
                    </div>
                  )}
                  <div className="report-form__editor-wrapper" style={{ position: "relative", flex: 1, minHeight: 0 }}>
                    {lockAvailable && !isEditorLocked && (
                      <div style={{ position: "absolute", inset: 0, zIndex: 5, cursor: "text" }} onClick={() => { if (!formValues?.id) return; ReportService.acquireReportLock(formValues.id, () => { currentUserHasLockRef.current = true; setCurrentUserHasLock(true); setIsEditorLocked(false); setLockAvailable(false); setAssignmentChangedWarning(null); }, () => {}, () => {}); }} />
                    )}
                    <div
                      aria-readonly={isEditorLocked || lockCheckLoading || undefined}
                      style={{
                        height: "100%",
                        ...(isEditorLocked || lockCheckLoading
                          ? { pointerEvents: "none", opacity: 0.6, userSelect: "none" }
                          : null),
                      }}
                    >
                      {!isTinyEditorEnabled ? (
                        <CKEditor error={errors.content as string} value={values.content || ""} onChangeHandler={(c) => { if (isProgrammaticUpdateRef.current) return; isDirtyRef.current = true; lastContentChangeAtRef.current = Date.now(); setFieldValue("content", c); scheduleAutoSaveRef.current?.(); }} editorInstanceRef={editorInstanceRef} viewerDarkMode={editorViewerDarkMode} />
                      ) : (
                        <Editor error={errors.content as string} value={values.content || ""} onChangeHandler={(c) => { if (isProgrammaticUpdateRef.current) return; isDirtyRef.current = true; lastContentChangeAtRef.current = Date.now(); setFieldValue("content", c); scheduleAutoSaveRef.current?.(); }} editorInstanceRef={editorInstanceRef} onSaveShortcut={!formValues?.status ? () => formikRef.current?.submitForm() : undefined} macros={reportMacros.map((m) => ({ shortname: m.shortname || "", expandedText: m.expandedText || "" }))} onAddMacro={(userRole === UserRoleEnum.ADMIN || userRole === UserRoleEnum.DOCTOR) ? () => { setActiveMacroForAdd(new ReportMacro()); setAddMacroModalVisible(true); } : undefined} fullscreenHeight={true} viewerDarkMode={editorViewerDarkMode} />
                      )}
                    </div>
                  </div>
                  {(aiReportLoading || usgReportLoading) && (
                    <div className={`report-form__ai-blocker${viewerReportChrome ? " report-form__ai-blocker--viewer" : ""}`} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, display: "flex", justifyContent: "center", alignItems: "center", pointerEvents: "none" }}>
                      <Spin size="large" style={{ color: "#1747E9" }} tip="Radiolinq AI Assist is preparing your radiology report" />
                    </div>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          // Fullscreen but report already published - left pane with download only
          <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
            <div className="report-form__left-pane">
              <div className="left-pane-section">
                <strong style={{ fontSize: 15, lineHeight: 1.4 }}>{caseDetail?.patientName}</strong>
                <span style={{ fontSize: 13, color: "#595959" }}>{caseDetail?.age} / {caseDetail?.gender}</span>
                <span style={{ display: "inline-block", fontSize: 12, fontWeight: 600, background: "#e6f0ff", color: "#1747E9", borderRadius: 4, padding: "2px 8px", alignSelf: "flex-start" }}>{caseDetail?.scanTypeName}</span>
              </div>
              <div className="left-pane-divider" />
              <div className="left-pane-section">
                <span className="left-pane-label">Report Actions</span>
                {formValues?.id && (
                  <Popover trigger="click" content={
                    <Menu mode="vertical">
                      <Menu.Item key="view" onClick={() => handleViewReport(caseDetail, formValues?.id)}>View Report</Menu.Item>
                      <Menu.Item key="pdf-lh" onClick={handleDownloadReport(formValues?.status, DownloadFileTypeEnum.PDF, true, formValues?.id)}>Download with Letterhead</Menu.Item>
                      <Menu.Item key="pdf" onClick={handleDownloadReport(formValues?.status, DownloadFileTypeEnum.PDF, false, formValues?.id)}>Download without Letterhead</Menu.Item>
                      <Menu.Item key="word" onClick={handleDownloadReport(formValues?.status, DownloadFileTypeEnum.WORD, false, formValues?.id)}>Download as Word</Menu.Item>
                    </Menu>
                  }>
                    <Button type="primary" loading={pdfLoading} block><DownloadOutlined /> Download</Button>
                  </Popover>
                )}
              </div>
            </div>
            <div className="report-form__editor-area" style={{ alignItems: "center", justifyContent: "center" }}>
              <div style={{ color: "#8c8c8c", fontSize: 14 }}>Report published</div>
            </div>
          </div>
        )
      ) : (
        <div>
          <Row gutter={[20, 0]}>
            <Col span={24}>
              <div
                className={`report-form__header ${onClose ? "report-form__header--in-drawer" : ""} ${
                  onClose && formValues?.status ? "report-form__header--report-published" : ""
                }`}
              >
                <div className="report-form__title">
                  {caseDetail?.patientName} | {caseDetail?.age} / {caseDetail?.gender} | {caseDetail?.scanTypeName}
                </div>
                {!formValues?.status &&
                  caseDetail?.status !== CaseStatusEnum.REPORTED &&
                  (userRole === UserRoleEnum.ADMIN ||
                    userRole === UserRoleEnum.SCAN_CENTER ||
                    (userRole === UserRoleEnum.DOCTOR &&
                      userFeatureNames.includes(DoctorFeatureEnum.TYPIST_LOGIN))) && (
                  <Tooltip title="Edit Case">
                    <Button
                      type={viewerReportChrome ? "text" : undefined}
                      className="ml-2"
                      icon={<EditOutlined />}
                      onClick={() => setShowEditCaseModal(true)}
                    />
                  </Tooltip>
                )}
                {!formValues?.status && !isTypistLogin && !isDoctorWithApprovalRequired && (
                    <div style={assignmentChangedWarning ? { pointerEvents: "none", opacity: 0.6 } : undefined}>
                    <UploadDirectReportButton
                        reportId={formValues?.id}
                        caseDetail={caseDetail}
                        size={onClose ? "small" : undefined}
                        onSuccess={(report: Report) => {
                            setFormValues(report);
                            onPublish?.();
                        }}
                    />
                    </div>
                )}
                {formValues?.id ? (
                  formValues?.status ? (
                      <Popover
                          destroyTooltipOnHide
                          overlayClassName={`admin-cases__assignment-popover${
                            viewerReportChrome ? " report-form-viewer-drawer-popover" : ""
                          }`}
                          placement="bottomLeft"
                          trigger="click"
                          content={
                              <Menu mode="vertical" className="border-none">
                                  {formValues?.directReportUpload ? (
                                      <Menu.Item
                                          key="view"
                                          onClick={() =>
                                              handleDownloadDirectReport(caseDetail, formValues?.id)
                                          }
                                      >
                                          Download Uploaded Report
                                      </Menu.Item>
                                  ) : (
                                      <>
                                          <Menu.Item
                                              key="view"
                                              onClick={() =>
                                                  handleViewReport(caseDetail, formValues?.id)
                                              }
                                          >
                                              View Report
                                          </Menu.Item>

                                          <Menu.Item
                                              key="with_letterhead"
                                              onClick={handleDownloadReport(
                                                  formValues?.status,
                                                  DownloadFileTypeEnum.PDF,
                                                  true,
                                                  formValues?.id
                                              )}
                                          >
                                              Download with Letterhead
                                          </Menu.Item>

                                          <Menu.Item
                                              key="without_letterhead"
                                              onClick={handleDownloadReport(
                                                  formValues?.status,
                                                  DownloadFileTypeEnum.PDF,
                                                  false,
                                                  formValues?.id
                                              )}
                                          >
                                              Download without Letterhead
                                          </Menu.Item>

                                          <Menu.Item
                                              key="word"
                                              onClick={handleDownloadReport(
                                                  formValues?.status,
                                                  DownloadFileTypeEnum.WORD,
                                                  false,
                                                  formValues?.id
                                              )}
                                          >
                                              Download as Word
                                          </Menu.Item>
                                      </>
                                  )}
                              </Menu>
                          }
                      >
                          <Button type="primary" size={onClose ? "small" : undefined} loading={pdfLoading}>
                              <DownloadOutlined /> Download
                          </Button>
                      </Popover>
                  ) : (
                      <Tooltip title="Preview PDF" placement="top">
                          <Button
                              type="default"
                              size={onClose ? "small" : undefined}
                              loading={pdfLoading}
                              disabled={!!assignmentChangedWarning}
                              icon={<EyeOutlined />}
                              onClick={handleDownloadReport(
                                  formValues?.status,
                                  DownloadFileTypeEnum.PDF,
                                  false,
                                  formValues?.id
                              )}
                          >
                            Preview
                          </Button>
                      </Tooltip>
                  )
              ) : null}
              {!isFullScreen && !(onClose && formValues?.status) && (
                <div className="report-form__full-screen-button" style={assignmentChangedWarning ? { pointerEvents: "none", opacity: 0.5 } : undefined}>
                  <Tooltip title="Fullscreen">
                    <Button
                      icon={<FullscreenOutlined />}
                      disabled={!!assignmentChangedWarning}
                      onClick={(e) => {
                        e.stopPropagation();
                        const path = generatePath(AppRoutes.GENERATE_REPORT, {
                          caseId: String(caseDetail?.id),
                        });
                        const returnSearch = location.search
                          ? `?returnSearch=${encodeURIComponent(location.search.slice(1))}`
                          : "";
                        window.open(
                          window.location.origin + path + returnSearch,
                          "_blank"
                        );
                        setTimeout(() => onClose?.(), 0);
                      }}
                    />
                  </Tooltip>
                </div>
              )}
              </div>
            </Col>
          </Row>
          {!formValues?.status && (
            <Formik
              innerRef={formikRef}
              initialValues={formValues}
              onSubmit={handleSubmit}
              enableReinitialize
              validationSchema={reportFormValidation}
            >
              {({ values, errors, isValid, setFieldValue }) => {
                let screenWidth = window.innerWidth;

                const radAiAssistToolbar = isAiReportingEnabled ? (
                  <>
                    {isAiDiagnosisEnabled ? (
                      aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string) && (
                        <Button
                          type="primary"
                          loading={usgReportLoading}
                          disabled={disableUsgReporting || isEditorLocked}
                          onClick={() => {
                            generateUsgReport(values, setFieldValue);
                          }}
                          style={{ marginRight: 5 }}
                        >
                          Generate USG Report
                        </Button>
                      )
                    ) : null}
                    <Button
                      type="default"
                      loading={aiReportLoading}
                      disabled={disableAiReporting || isEditorLocked}
                      onClick={() => {
                        if (!isModalOpen) {
                          smartReporting(values, setFieldValue);
                        } else {
                          setIsModalOpen(false);
                          setIsMicLoading(false);
                          stopSpeechRecognition();
                        }
                      }}
                    >
                      {!isModalOpen ? "Try RadAI Assist" : "Close RadAI Assist"}
                    </Button>
                    <Tooltip
                      title={
                        <div>
                          <p>
                            <strong>How to use RadAI Assist?</strong>
                          </p>
                          <ul style={{ paddingLeft: 16 }}>
                            <li>Start by dictating findings and include the template name if needed</li>
                            <li>
                              Click on <em>Try RadAI Assist</em>
                            </li>
                            <li>Enter your key findings or observations</li>
                            <li>
                              Click on <em>Generate Smart Report</em>
                            </li>
                            <li>Let RadAI Assist craft a detailed, intelligent report for you</li>
                          </ul>
                        </div>
                      }
                      placement="top"
                    >
                      <QuestionCircleOutlined
                        className={viewerReportChrome ? "report-form__viewer-help-icon" : undefined}
                        style={{
                          fontSize: 16,
                          marginLeft: 8,
                          cursor: "pointer",
                          ...(viewerReportChrome ? {} : { color: "#1747E9" }),
                        }}
                      />
                    </Tooltip>
                    <span style={{ marginLeft: "10px", marginRight: "10px" }}>
                      <span>
                        {aiCreditsUsed} / {aiTotalCredits} credits
                      </span>
                    </span>
                  </>
                ) : null;

                const publishDraftControls = (
                  <>
                    {values?.id &&
                      !values.status &&
                      (user?.approvalRequired && (caseDetail?.approvalRequiredForCase ?? true) ? (
                        <Button
                          className="mr-2"
                          type="primary"
                          htmlType="button"
                          loading={publishLoading}
                          disabled={publishLoading || isEditorLocked || lockCheckLoading}
                          onClick={handleSendForApproval(values)}
                        >
                          Send for Approval
                        </Button>
                      ) : caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED && userRole === UserRoleEnum.ADMIN ? (
                        <Button
                          className="mr-2"
                          type="primary"
                          htmlType="button"
                          loading={publishLoading}
                          disabled={publishLoading || isEditorLocked || lockCheckLoading}
                          onClick={handleApproveAndPublishReport(values)}
                        >
                          Approve and Publish Report
                        </Button>
                      ) : !isTypistLogin ? (
                        <Button
                          className="mr-2"
                          type="primary"
                          htmlType="button"
                          loading={publishLoading}
                          disabled={publishLoading || isEditorLocked || lockCheckLoading}
                          icon={<CheckOutlined />}
                          onClick={handlePublishReport(values)}
                        >
                          Save & Publish
                        </Button>
                      ) : null)}
                    {!values.status && (
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={formLoading}
                        disabled={!isValid || formLoading || isEditorLocked || lockCheckLoading}
                        icon={<SaveOutlined />}
                      >
                        Save Draft
                      </Button>
                    )}
                  </>
                );

                // @ts-ignore
                return (
                  <Form>
                    <Row gutter={[10, 10]} align="middle">
                      <Col span={fromViewer || (isFullScreen && isMobile) ? 24 : 6} xs={24} lg={fromViewer || (isFullScreen && isMobile) ? 24 : 6} xl={fromViewer || (isFullScreen && isMobile) ? 24 : 6} xxl={fromViewer || (isFullScreen && isMobile) ? 24 : 6}>
                        <DropdownField
                          allowClear
                          placeHolder="Select Template"
                          showSearch
                          name="reportTemplateId"
                          disabled={isEditorLocked || lockCheckLoading}
                          onSearch={(value) => {
                            setSearchTerm(value);
                          }}
                          onClear={() => {
                            setSearchTerm("");
                          }}
                          onChange={(value) => {
                            setFieldValue("reportTemplateId", value);
                            const template = reportTemplates.find(
                              (template) => template.id === value
                            );
                            if (template) {
                              setFieldValue(
                                "content",
                                values.content
                                  ? values?.content + "<br/>" + template.content
                                  : template.content
                              );
                            }
                          }}
                          options={reportTemplateOptions}
                          dropdownClassName={
                            viewerReportChrome ? "report-form-viewer-drawer-dropdown" : undefined
                          }
                        />
                      </Col>

                      <Col
                        span={fromViewer || (isFullScreen && isMobile) ? 24 : 12}
                        xs={24}
                        lg={fromViewer || (isFullScreen && isMobile) ? 24 : 12}
                        xl={fromViewer || (isFullScreen && isMobile) ? 24 : 12}
                        xxl={fromViewer || (isFullScreen && isMobile) ? 24 : 12}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <Switch
                            checked={isTinyEditorEnabled}
                            onChange={(checked) => setTinyEditorEnabled(checked)}
                            style={{ marginRight: 8, marginTop: 2 }}
                            disabled={aiReportLoading || disableUsgReporting || isEditorLocked || lockCheckLoading}
                          />
                          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            Use New Editor <Tag color="green" style={{ padding: "0 4px", fontSize: 10 }}>New</Tag>
                          </span>
                        </div>
                      </Col>
                      {isAiDiagnosisEnabled ? (
                        aiDiagnosisTypes.includes(caseDetail?.scanTypeName as string) &&
                        (fromViewer ? (
                          <Col span={24}>
                            <div className="report-form__drawer-usg-fields">
                              <DropdownField
                                allowClear
                                placeHolder="Select Organ"
                                showSearch
                                title="Organ"
                                name="organId"
                                onChange={(value) => {
                                  setFieldValue("organId", value);
                                  handleOrganSelect(value);
                                }}
                                options={organOptions}
                                dropdownClassName={
                                  viewerReportChrome ? "report-form-viewer-drawer-dropdown" : undefined
                                }
                              />
                              <DropdownField
                                allowClear
                                placeHolder="Select Detail"
                                showSearch
                                title="Pathology Detail"
                                name="detailId"
                                onChange={(value) => {
                                  setFieldValue("detailId", value);
                                  handlePathologyDetail(value);
                                }}
                                options={subOptions}
                                disabled={!selectedOrgan}
                                dropdownClassName={
                                  viewerReportChrome ? "report-form-viewer-drawer-dropdown" : undefined
                                }
                              />
                            </div>
                          </Col>
                        ) : (
                          <>
                            <Col span={12} />
                            <Col span={7} style={{ marginTop: -10 }}>
                              <DropdownField
                                allowClear
                                placeHolder="Select Organ"
                                showSearch
                                title="Organ"
                                name="organId"
                                onChange={(value) => {
                                  setFieldValue("organId", value);
                                  handleOrganSelect(value);
                                }}
                                options={organOptions}
                              />
                            </Col>
                            <Col span={15} style={{ marginLeft: 5, marginTop: -10 }}>
                              <DropdownField
                                allowClear
                                placeHolder="Select Detail"
                                showSearch
                                title="Pathology Detail"
                                name="detailId"
                                onChange={(value) => {
                                  setFieldValue("detailId", value);
                                  handlePathologyDetail(value);
                                }}
                                options={subOptions}
                                disabled={!selectedOrgan}
                              />
                            </Col>
                          </>
                        ))
                      ) : null}
                      {fromViewer ? (
                        <Col span={24} className="report-form__drawer-action-row">
                          {lastAutoSavedAt && (
                            <div className="report-form__drawer-autosave">
                              {showSavedTick ? (
                                <div className="report-form__autosave-badge report-form__autosave-badge--visible report-form__autosave-badge--drawer">
                                  <span className="report-form__autosave-tick">
                                    <svg viewBox="0 0 20 20" fill="none">
                                      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" className="report-form__autosave-circle" />
                                      <polyline points="5.5,10.5 8.5,13.5 14.5,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="report-form__autosave-check" />
                                    </svg>
                                  </span>
                                  <span>Auto saved</span>
                                </div>
                              ) : (
                                <span className="report-form__autosave-static report-form__autosave-static--drawer">
                                  Last saved at {lastAutoSavedAt.toLocaleTimeString()}
                                </span>
                              )}
                            </div>
                          )}
                          <div className="report-form__drawer-actions-inner">
                            <div className="report-form__drawer-actions-left">{radAiAssistToolbar}</div>
                            <div className="report-form__drawer-actions-right">
                              <div className="report-form__submit-wrapper report-form__submit-wrapper--drawer">{publishDraftControls}</div>
                            </div>
                          </div>
                        </Col>
                      ) : (
                        <>
                          <Col
                            span={isFullScreen && isMobile ? 24 : 12}
                            xs={24}
                            lg={isFullScreen && isMobile ? 24 : 12}
                            xl={isFullScreen && isMobile ? 24 : 12}
                            xxl={isFullScreen && isMobile ? 24 : 12}
                            className="report-form__ai-assist"
                          >
                            <React.Fragment>{radAiAssistToolbar}</React.Fragment>
                          </Col>
                        </>
                      )}
                      {isModalOpen && isVoiceToTextEnabled && (
                          <>
                              {/* -------- TEXT AREA -------- */}
                              <Col span={24}>
                                <Input.TextArea
                                  className={!isDeepgram ? "report-detail__ai-textarea" : ""}
                                  rows={4}
                                  placeholder="Enter your key findings or observations"
                                  value={
                                      isDeepgram
                                          ? textareaValue
                                          : baseValue + (liveTranscript || "")
                                  }
                                  onChange={(e) => {
                                      if (isDeepgram) {
                                          setTextareaValue(e.target.value);
                                      } else {
                                          setBaseValue(e.target.value);
                                          setTextareaValue(e.target.value);
                                      }
                                  }}
                                  style={{
                                      color: !isDeepgram && liveTranscript ? "#999" : "inherit",
                                  }}
                                />
                              </Col>
                              <Col span={24}>
                                {/* -------- MIC + TOOLTIP + TIMER -------- */}
                                <React.Fragment>
                                    <Button
                                        type="primary"
                                        loading={isMicLoading}
                                        onClick={() =>
                                            handleMicClick(values, setFieldValue)
                                        }
                                    >
                                        {isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
                                    </Button>

                                    <Tooltip title={voiceTooltip} placement="top">
                                        <QuestionCircleOutlined
                                            style={{
                                                marginTop: 10,
                                                fontSize: 16,
                                                marginLeft: 8,
                                                cursor: "pointer",
                                                color: "#1747E9",
                                            }}
                                        />
                                    </Tooltip>

                                    <span
                                        style={{
                                            marginTop: "7px",
                                            marginLeft: "10px",
                                            marginRight: "10px",
                                        }}
                                    >
                                    {formatTime(timer)} / {formatTime(totalTimer)}
                                  </span>
                                </React.Fragment>

                                {/* -------- ACTION BUTTONS -------- */}
                                <Button
                                    key="submit"
                                    type="primary"
                                    style={{ marginRight: "10px" }}
                                    onClick={() => {
                                        if (!textareaValue.length) {
                                            alert(
                                                "Please enter proper key findings or observations for generating report."
                                            );
                                            return;
                                        }

                                        generateGeminiReport(textareaValue, values);
                                        setIsModalOpen(false);
                                    }}
                                >
                                    Generate Smart Report
                                </Button>
                              </Col>
                          </>
                      )}

                        {!fromViewer && (
                          <Col span={12} xs={24}>
                            <div className="report-form__submit-wrapper">
                              {lastAutoSavedAt && showSavedTick && (
                                <div className="report-form__autosave-badge report-form__autosave-badge--visible">
                                  <span className="report-form__autosave-tick">
                                    <svg viewBox="0 0 20 20" fill="none">
                                      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5" className="report-form__autosave-circle" />
                                      <polyline points="5.5,10.5 8.5,13.5 14.5,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="report-form__autosave-check" />
                                    </svg>
                                  </span>
                                  <span>Auto saved</span>
                                </div>
                              )}
                              {lastAutoSavedAt && !showSavedTick && (
                                <span className="report-form__autosave-static">
                                  Last saved at {lastAutoSavedAt.toLocaleTimeString()}
                                </span>
                              )}
                              {publishDraftControls}
                            </div>
                          </Col>
                        )}

                        <Col span={24} className="report-form__editor-col">
                          {assignmentChangedWarning && (
                            <div className="report-form__notice report-form__notice--danger">
                              <span className="report-form__notice-text">{warningMessage}</span>
                              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                <Button
                                  size="small"
                                  type="primary"
                                  danger
                                  onClick={warningActionHandler}
                                >
                                  {warningActionLabel}
                                </Button>
                                {assignmentChangedWarning === "updated" && (
                                  <Button
                                    size="small"
                                    onClick={() => setAssignmentChangedWarning(null)}
                                  >
                                    Dismiss
                                  </Button>
                                )}
                              </div>
                            </div>
                          )}
                          {lockCheckLoading && (
                            <div className="report-form__muted-line report-form__muted-line--spin">
                              <Spin size="small" style={{ marginRight: 6 }} />
                              Checking editor availability…
                            </div>
                          )}
                          {isEditorLocked && !assignmentChangedWarning && (
                            <div className="report-form__notice report-form__notice--lock">
                              <span className="report-form__notice-text">
                                🔒 {lockedByInfo?.display_name ?? "Someone"} is currently editing this report
                              </span>
                            </div>
                          )}
                          {lockAvailable && !isEditorLocked && (
                            <div className="report-form__notice report-form__notice--available">
                              Editor is now available — click anywhere in the editor to start editing
                            </div>
                          )}
                          {lastDraftSavedBy && (
                            <div className="report-form__muted-line">
                              Last saved by {lastDraftSavedBy.displayName} at{" "}
                              {new Date(lastDraftSavedBy.savedAt).toLocaleTimeString()}
                            </div>
                          )}
                          <div style={{ position: "relative" }}>
                            {lockAvailable && !isEditorLocked && (
                              <div
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  zIndex: 5,
                                  cursor: "text",
                                }}
                                onClick={() => {
                                  if (!formValues?.id) return;
                                  ReportService.acquireReportLock(
                                    formValues.id,
                                    () => {
                                      currentUserHasLockRef.current = true;
                                      setCurrentUserHasLock(true);
                                      setIsEditorLocked(false);
                                      setLockAvailable(false);
                                      setAssignmentChangedWarning(null);
                                    },
                                    () => {},
                                    () => {}
                                  );
                                }}
                              />
                            )}
                          <div
                            aria-readonly={isEditorLocked || lockCheckLoading || undefined}
                            style={
                              isEditorLocked || lockCheckLoading
                                ? { pointerEvents: "none", opacity: 0.6, userSelect: "none" }
                                : undefined
                            }
                          >
                            {!isTinyEditorEnabled && (
                                <CKEditor
                                    error={errors.content as string}
                                    value={values.content || ""}
                                    onChangeHandler={(content) => {
                                        if (isProgrammaticUpdateRef.current) return;
                                        isDirtyRef.current = true;
                                        lastContentChangeAtRef.current = Date.now();
                                        setFieldValue("content", content);
                                        scheduleAutoSaveRef.current?.();
                                    }}
                                    editorInstanceRef={editorInstanceRef}
                                    viewerDarkMode={editorViewerDarkMode}
                                />
                            )}
                            {isTinyEditorEnabled && (
                                <Editor
                                    error={errors.content as string}
                                    value={values.content || ""}
                                    onChangeHandler={(content) => {
                                        if (isProgrammaticUpdateRef.current) return;
                                        isDirtyRef.current = true;
                                        lastContentChangeAtRef.current = Date.now();
                                        setFieldValue("content", content);
                                        scheduleAutoSaveRef.current?.();
                                    }}
                                    editorInstanceRef={editorInstanceRef}
                                    onSaveShortcut={
                                        !formValues?.status
                                            ? () => formikRef.current?.submitForm()
                                            : undefined
                                    }
                                    macros={reportMacros.map((m) => ({ shortname: m.shortname || "", expandedText: m.expandedText || "" }))}
                                    onAddMacro={(userRole === UserRoleEnum.ADMIN || userRole === UserRoleEnum.DOCTOR) ? () => { setActiveMacroForAdd(new ReportMacro()); setAddMacroModalVisible(true); } : undefined}
                                    fullscreenHeight={false}
                                    height="65vh"
                                    viewerDarkMode={editorViewerDarkMode}
                                />
                            )}
                          </div>
                          {(aiReportLoading || usgReportLoading) && (
                          <div
                            className={`report-form__ai-blocker${viewerReportChrome ? " report-form__ai-blocker--viewer" : ""}`}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              zIndex: 10,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              pointerEvents: "none", // Allows click-through for readOnly editor
                            }}
                          >
                            <Spin
                              size="large"
                              style={{ color: "#1747E9" }}
                              tip="Radiolinq AI Assist is preparing your radiology report"
                            />
                          </div>
                        )}
                          </div>
                      </Col>
                    </Row>
                  </Form>
                );
              }}
            </Formik>
          )}
        </div>
      )}
      <Modal
        className="primary-modal"
        footer=""
        keyboard={false}
        maskClosable={false}
        destroyOnClose
        visible={addMacroModalVisible}
        onCancel={() => setAddMacroModalVisible(false)}
      >
        {activeMacroForAdd && (
          <ReportMacroForm
            reportMacro={activeMacroForAdd}
            onSuccess={() => {
              setAddMacroModalVisible(false);
              handleFetchReportMacros();
            }}
          />
        )}
      </Modal>
      <Modal
        destroyOnClose
        visible={showEditCaseModal}
        className="primary-modal"
        onCancel={() => setShowEditCaseModal(false)}
        footer={null}
        maskClosable={false}
      >
        {caseDetail?.id && (
          <CaseForm
            caseDetailId={caseDetail.id}
            onSuccess={(updatedCase: CaseDetail) => {
              setCaseDetail(updatedCase);
              setShowEditCaseModal(false);
            }}
            onClose={() => setShowEditCaseModal(false)}
          />
        )}
      </Modal>
      <Modal
        title="Report Preview"
        visible={previewExpandedVisible}
        onCancel={() => setPreviewExpandedVisible(false)}
        footer={null}
        width="100%"
        style={{ top: 0, paddingBottom: 0 }}
        bodyStyle={{ height: "calc(100vh - 55px)", padding: 0 }}
        destroyOnClose
      >
        {previewPdfUrl && (
          <iframe
            src={previewPdfUrl}
            title="Report preview fullscreen"
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        )}
      </Modal>
    </div>
  );
}

export default AuthContainer(ReportLockContainer(ReportForm));
